/**
 * Action creators for settings pages
 *
 * @prettier
 * @author Alexander Nagelberg
 * @since 24.3
 */

import { Dispatch } from 'redux'
import { SettingsActionType } from '../reducers/settingsReducer'
import { catchCommonErrors } from 'app/actions/commonActionUtils'
import { getCookieValue } from 'app/actions/genericActionCreator'
import { SubmissionError } from 'redux-form'
import { convertLgyErrorResponseToFormErrorObject } from 'app/utils/formUtils'
import { SettingsAPIURL, replaceTokens } from './settingsConfig'
import { ISettingsPage } from '../types/ISettingsPage'
import { SettingFieldType } from '../types/SettingFieldType'
import { IUpdateSettingsRequest } from '../types/IUpdateSettingsRequest'

const transformCheckboxBooleansInPage = (payload: any): ISettingsPage => ({
  ...payload,
  sections: (payload as ISettingsPage).sections?.map(({ headers, settings, ...other }) => ({
    headers: headers?.sort(({ order: a }, { order: b }) => a - b),
    settings: settings?.map(({ options, ...other }) => ({
      options: options
        .sort(({ order: a }, { order: b }) => a - b)
        .map(({ fieldType, value, ...other }) => ({
          fieldType,
          value: fieldType === SettingFieldType.CheckBox ? value === 'true' : value,
          ...other
        })),
      ...other
    })),
    ...other
  }))
})

const genericGET = ({
  URL,
  fetchingAction,
  fetchedAction
}: {
  URL: string
  fetchingAction: SettingsActionType
  fetchedAction: SettingsActionType
}) => async (dispatch: Dispatch) => {
  dispatch({ type: fetchingAction })
  const response = await fetch(URL)
  const responseJson = await catchCommonErrors(dispatch, response, 'fetching settings')
  if (response.status < 400) {
    // Does a sort and special transform for values for checkbox (casts strings of 'true'/'false' to boolean true/false)
    const data = responseJson instanceof Array ? responseJson : transformCheckboxBooleansInPage(responseJson)
    dispatch({ type: fetchedAction, data })
  } else {
    console.error(responseJson)
  }
}

/**
 * Simple action creator to fetch all pages and sections (titles)
 */
export const fetchAllPages = () =>
  genericGET({
    URL: SettingsAPIURL.FetchAllPages,
    fetchingAction: SettingsActionType.FetchingSettings,
    fetchedAction: SettingsActionType.FetchedPages
  })

export const fetchSettings = (id: string) =>
  genericGET({
    URL: replaceTokens(SettingsAPIURL.FetchPage, { pageId: id }),
    fetchingAction: SettingsActionType.FetchingSettings,
    fetchedAction: SettingsActionType.FetchedPage
  })

export const clearPage = () => (dispatch: Dispatch) => dispatch({ type: SettingsActionType.InitPage })

export const submitOptions = (request: IUpdateSettingsRequest) => async (dispatch: Dispatch) => {
  dispatch({ type: SettingsActionType.PostingSettings })
  try {
    const response = await fetch(SettingsAPIURL.UpdateOptions, {
      credentials: 'include',
      method: 'PATCH',
      body: JSON.stringify(request),
      headers: new Headers({
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN') as string
      })
    })
    const responseJson = await catchCommonErrors(dispatch, response, 'updating setting options')
    if (response.status >= 400) {
      dispatch({ type: SettingsActionType.ErrorPostingSettings })
      throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
    } else {
      const data = responseJson instanceof Array ? responseJson : transformCheckboxBooleansInPage(responseJson)
      dispatch({ type: SettingsActionType.PostedSettings, data })
    }
  } catch (e) {
    if (e instanceof SubmissionError) {
      throw e
    }
    console.error(e)
  }
}
